/**
 * `overlayToAddressDecoration` — the ONE reducer from a precedence-RESOLVED
 * {@link ResolvedOverlays} bundle down to the neutral {@link AddressOverlay}
 * decoration the transpose adapters (graph / scene) stamp onto a node.
 *
 * Every surface that paints an address with its catalog identity — the tx graph
 * nodes, the semantic scene, the flow graph, the address panel/header chips —
 * goes through here, so the "which label / logo / colour / entity wins" choice
 * is made in exactly ONE place and can't drift between surfaces. The overlays
 * are already chain-scoped by the fetch (the batch hook resolves per
 * `(chain, address)`), so this is a pure pick over the resolved bundle.
 *
 * Precedence within the bundle: a curated PRIMARY of a kind wins over the merge
 * leader, which wins over the first hit. Branding and group identities use the shared catalog projection,
 * so the same address is decorated consistently across graph surfaces.
 *
 * RISK is the exception to that precedence, deliberately: it takes the WORST
 * severity across every label and tag rather than the leading one. A curated
 * primary exists to decide what an address is *called*, and letting it decide
 * how dangerous the address is would let "Exchange" outrank "Fake_Phishing" on
 * an address carrying both.
 */
import { addressFactsOverlay, addressRisk, catalogRisk, toAddressOverlay, type AddressFactsRow, type RiskBearingRow, type ResolvedOverlays } from "@authe/fe-commons/overlays";
import type { AddressOverlay } from "@authe/fe-commons/transpose";

export interface OverlayDecorationOptions {
  /**
   * Turn a stored logo key into a renderable URL (the catalog logos are S3 keys,
   * not URLs). Pass `catalog.resolveLogoUrl`; when omitted the raw key is kept
   * (fine for already-absolute URLs, best-effort otherwise).
   */
  resolveLogoUrl?: (key: string) => string | undefined;
  /**
   * The address's stored row, when the batch was asked for one.
   *
   * Carries the half of risk the catalog cannot see: a band an analyst set by
   * hand. Without it a node whose whole risk is somebody's own judgement drew
   * exactly like an unassessed one — the analyst set `severe` on the address and
   * the graph did not change colour, which is the one place colour IS the
   * signal.
   */
  row?: DecorationRow | null;
}

/**
 * The stored row as this reducer reads it: risk, plus the registry's own NAME.
 *
 * `RiskBearingRow` is the narrow risk view; the value every caller actually
 * passes is the whole `Address`, so the name is already in hand.
 */
export type DecorationRow = NonNullable<RiskBearingRow> & AddressFactsRow & { name?: unknown };

/**
 * The registry's name for the address, when it says anything.
 *
 * This is the field the address page titles itself with (`AddressDetailPage`'s
 * `headerName`), and the reason a decoded argument could read as bare hex on a
 * page whose own Addresses card, and whose address page one click away, both
 * said "Wrapped Ether": the name lives on the ROW, and only the catalog
 * overlays were being reduced. A row is only fetched with `includeRow`, so this
 * costs nothing where no caller asked for one.
 *
 * Skipped when it merely restates the address — some providers fill the name
 * with the hex — which would render as a second copy of what is already there.
 */
function rowName(row: DecorationRow | null | undefined): string | undefined {
  const name = typeof row?.name === "string" ? row.name.trim() : "";
  if (!name) return undefined;
  const bare = name.replace(/^0x/i, "").toLowerCase();
  return /^[0-9a-f]{40,64}$/.test(bare) ? undefined : name;
}

/**
 * Collapse a resolved bundle into the node decoration. Returns `undefined` when
 * the bundle carries nothing to paint, so callers can leave a node bare rather
 * than stamp an empty overlay.
 */
export function overlayToAddressDecoration(
  overlays: ResolvedOverlays | null | undefined,
  opts: OverlayDecorationOptions = {},
): AddressOverlay | undefined {
  // A row alone is enough to decorate with: an address the catalog has nothing
  // curated about still has a registry name and a recorded risk band, and
  // returning early on the empty bundle is what dropped both.
  if (!overlays && !opts.row) return undefined;
  // The same catalog projection as every graph/scene surface: includes stable
  // catalog-group identities, curated primary picks, names and branding.
  const { riskScoreBasis: _basis, ...projected } = toAddressOverlay(overlays);
  const decoration: AddressOverlay = {
    ...addressFactsOverlay(opts.row),
    ...Object.fromEntries(Object.entries(projected).filter(([, value]) =>
      value != null && (!Array.isArray(value) || value.length > 0))),
  };
  if (!decoration.label) {
    const name = rowName(opts.row);
    if (name) decoration.label = name;
  }
  if (decoration.logo) decoration.logo = opts.resolveLogoUrl?.(decoration.logo) ?? decoration.logo;

  // Labels AND tags: an accusation arrives as either, depending on which
  // explorer chip the scraper read it from. Then the worst of that and the row's
  // own recorded band — `addressRisk` is the same rule the address page header
  // uses, so a node and that page cannot disagree about one address.
  const risk = addressRisk([opts.row], catalogRisk(overlays));
  if (risk) {
    decoration.riskLevel = risk.level;
    // Both, because `GraphNode` renders both: a badge showing "High" with a
    // blank beside it reads as a number that failed to load rather than one
    // nobody claimed.
    decoration.riskScore = risk.score;
    decoration.riskScoreBasis = "category";
  }

  return Object.keys(decoration).length > 0 ? decoration : undefined;
}
